import { Injectable } from '@angular/core';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { environment } from 'src/environments/environment';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AssetFilesService } from 'src/app/admin/services/asset-files.service';

@Injectable({
  providedIn: 'root'
})
export class SpreadsheetService {
  constructor(
    private assetManager: AssetManager,
    private http: HttpClient,
    private assetFilesService: AssetFilesService
  ) {

  }

   ImportSpreadSheet(file: File) {
    return this.publishInspectionReport(file).pipe(
      map(inspectionElements => this.sanitizeImportedElementsByAsset(inspectionElements)),
    )
  }

   ImportSpreadSheetByAgent(file: File) {
    // Dedicated entrypoint for agent flow. Do not sanitize by current opened asset.
    return this.publishInspectionReport(file).pipe(
      map(inspectionElements => inspectionElements ?? []),
    );
  }

  private publishInspectionReport(file: File): Observable<ElementResultInfo[] | null | undefined> {
    // Use ONLY the file for necessary metadata. DO NOT pass asset id or other info.
    return this.assetFilesService.createTemporarySpreadsheetUploadUrl().pipe(
      map(uploadUrl => ({
        uploadUrl,
        uploadedReportPath: this.extractUploadedReportPath(uploadUrl),
      })),
      switchMap(({ uploadUrl, uploadedReportPath }) =>
        from(this.assetFilesService.uploadFileToAzure(file, uploadUrl)).pipe(
          switchMap(() =>
            this.http.post<ElementResultInfo[]>(
              `${environment.API_INSPEC}Spreadsheet/publishinspectionreport`,
              null,
              {
                params: new HttpParams()
                  .set('uploadedReportPath', uploadedReportPath)
              }
            )
          )
        )
      ),
    );
  }

  private sanitizeImportedElementsByAsset(inspectionElements: ElementResultInfo[] | null | undefined): ElementResultInfo[] {
    if (!inspectionElements?.length) {
      return inspectionElements ?? [];
    }

    const currentAssetId = this.assetManager.asset?.assetInfo?.AssetId;
    if (!currentAssetId) {
      return [];
    }

    const hasDifferentAsset = inspectionElements.some(element =>
      !!element?.AssetId && element.AssetId !== currentAssetId
    );

    return hasDifferentAsset ? [] : inspectionElements;
  }

  private extractUploadedReportPath(uploadUrl: string): string {
    try {
      const pathname = decodeURIComponent(new URL(uploadUrl).pathname);
      const path = pathname.replace(/^\/+/, '');
      if (!path) {
        throw new Error('Empty upload path.');
      }
      return path;
    } catch {
      // Fallback parser for unexpected URL shapes.
      const withoutQuery = decodeURIComponent(uploadUrl.split('?')[0] ?? '');
      const noProtocol = withoutQuery.replace(/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//, '');
      const firstSlashIndex = noProtocol.indexOf('/');
      const path = firstSlashIndex >= 0 ? noProtocol.substring(firstSlashIndex + 1) : '';
      const normalizedPath = path.replace(/^\/+/, '');

      if (!normalizedPath) {
        throw new Error('Unable to extract uploaded report path from upload URL.');
      }

      return normalizedPath;
    }
  }

}
